import React from 'react'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { i18n } from '@fs/zion-locale'
import { useTranslation } from 'react-i18next'
import ZionForm, { FormTextField, useZionForm } from '@fs/zion-form'
import { BillboardButton, FlowGrid, usePageWidth } from '@fs/zion-ui'

export function getSearchUrl(values) {
  const {
    q_givenName,
    surname,
    q_anyPlace,
    q_birthLikeDate_from,
    collectionIds = [],
    goToOneSearch,
    goToRecordsTab,
  } = values
  const collectionIdParams =
    collectionIds.length === 1
      ? `&f.collectionId=${collectionIds[0]}`
      : collectionIds.map((id, index) => `&${encodeURIComponent(`f.collectionId[${index}]`)}=${id}`).join('')
  const url =
    q_givenName || q_anyPlace || q_birthLikeDate_from || collectionIds.length > 0 || goToOneSearch
      ? `/${i18n.language}/search/discovery/results?${goToRecordsTab ? 'tab=records&' : ''}results=12&q.givenName=${q_givenName ? encodeURIComponent(q_givenName) : ''}&q.surname=${surname ? encodeURIComponent(surname) : ''}&q.anyPlace=${q_anyPlace ? encodeURIComponent(q_anyPlace) : ''}&q.birthLikeDate.from=${q_birthLikeDate_from ? encodeURIComponent(q_birthLikeDate_from) : ''}${collectionIdParams}`
      : `/${i18n.language}/surname?surname=${surname ? encodeURIComponent(surname) : ''}`
  return url
}

export default function AncestorSearch({
  showLabels,
  color = 'blue',
  columns = 2,
  collectionIds = [],
  target = '_self',
  goToOneSearch,
  goToRecordsTab,
  ariaDescribedBy,
}) {
  const atWidth = usePageWidth()
  const [t] = useTranslation()
  const currentYear = new Date().getFullYear()
  const validMaxYearMessage = t(
    'nlihp.ancestor-search.birth-year-max-value.error',
    'Year cannot be greater than {currentYear}',
    { currentYear }
  )
  const validYearNumberMessage = t('nlihp.ancestor-search.birth-year.error', 'Year must be a number.')
  const validationSchema = Yup.object().shape({
    q_givenName: Yup.string(),
    surname: Yup.string().required(t('nlihp.ancestor-search.required', 'Required')),
    q_anyPlace: Yup.string(),
    q_birthLikeDate_from: Yup.number()
      .nullable()
      .integer(validYearNumberMessage)
      .typeError(validYearNumberMessage)
      .notOneOf([0], t('nlihp.ancestor-search.birth-year-zero.error', 'Year cannot be zero'))
      .min(-2000, t('nlihp.ancestor-search.birth-year-negative.error', 'Year cannot be less than -2000'))
      .max(currentYear, validMaxYearMessage)
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  })
  const defaultValues = {
    q_givenName: '',
    surname: '',
    q_anyPlace: '',
    q_birthLikeDate_from: '',
  }
  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(validationSchema),
    defaultValues,
  })
  const {
    formState: { isValid, isDirty },
  } = methods
  const onSubmit = (values) => {
    const url = getSearchUrl({ ...values, collectionIds, goToOneSearch, goToRecordsTab })
    if (target === '_self') {
      window.location.href = url
    } else {
      window.open(url, target)
    }
  }

  return (
    <ZionForm methods={methods} onSubmit={onSubmit}>
      <FlowGrid columnCount={atWidth({ default: 1, sm: columns || 2 })} guttersY="none">
        <SearchFormTextField
          label={showLabels ? t('nlihp.ancestor-search.first-names', 'First Names') : ''}
          aria-label={t('nlihp.ancestor-search.first-names', 'First Names')}
          placeholder={t('nlihp.ancestor-search.first-names', 'First Names')}
          name="q_givenName"
          aria-describedby={ariaDescribedBy}
        />
        <SearchFormTextField
          label={showLabels ? t('nlihp.ancestor-search.last-names.label', 'Last Names*') : ''}
          aria-label={t('nlihp.ancestor-search.last-names', 'Last Names (required)')}
          placeholder={t('nlihp.ancestor-search.last-names', 'Last Names (required)')}
          name="surname"
          aria-describedby={ariaDescribedBy}
        />
        <SearchFormTextField
          label={showLabels ? t('nlihp.ancestor-search.place-lived', 'Place Lived') : ''}
          aria-label={t('nlihp.ancestor-search.place-lived', 'Place Lived')}
          placeholder={t('nlihp.ancestor-search.place-lived', 'Place Lived')}
          name="q_anyPlace"
          aria-describedby={ariaDescribedBy}
        />
        <SearchFormTextField
          label={showLabels ? t('nlihp.ancestor-search.birth-year', 'Birth Year') : ''}
          aria-label={t('nlihp.ancestor-search.birth-year', 'Birth Year')}
          placeholder={t('nlihp.ancestor-search.birth-year', 'Birth Year')}
          name="q_birthLikeDate_from"
          aria-describedby={ariaDescribedBy}
        />
        <BillboardButton
          type="submit"
          disabled={!isValid || !isDirty}
          emphasis="high"
          color={color}
          linkName="Ancestor Search"
        >
          {t('nlihp.ancestor-search.search.action', 'Search')}
        </BillboardButton>
      </FlowGrid>
    </ZionForm>
  )
}

// Don't pass a label prop to FormTextField if the label prop is empty.
// This ensures that the component does not leave space for the label.
function SearchFormTextField({ label, ...props }) {
  return label ? <FormTextField label={label} {...props} /> : <FormTextField {...props} />
}
